import { useTranslation } from "react-i18next";

/**
 * The product area a change belongs to, shown as a small uppercase chip before the entry text
 * ("PAYROLL", "TIME TRACKING"). Optional: entries without a module render nothing at all, which
 * is the case for every release authored before the field existed.
 *
 * The API sends the **English module name**, not a code, and this component translates it with
 * `t(module)` directly. That looks like a shortcut but it is the reason the field needs no
 * translation work: the app's locale files are flat and keyed by the English string
 * ("Payroll": "Gehaltsabrechnung"), so a module the app already knows about is translated
 * everywhere it has a key, and one it doesn't falls back to the English name via i18next's
 * key-as-fallback. Contrast CategoryBadge, which maps three fixed codes onto
 * `patchNotes.category.*` keys — that set never grows, while modules do, and inventing a
 * `patchNotes.module.*` namespace would mean re-translating names the app already has.
 *
 * Coverage today is partial: de and sr cover most module names, pl and tr have gaps. A gap shows
 * the English name rather than an empty chip, and is fixed by adding the key to that locale, not
 * by changing anything here.
 */
interface ModuleChipProps {
    module?: string,
}

export default function ModuleChip({ module }: ModuleChipProps) {
    const { t } = useTranslation();

    if (!module?.trim()) {
        return null;
    }

    return (
        <span className="pn-item-module">{t(module)}</span>
    );
}
